import { useState, useCallback } from 'react';
import stauffApi from '../services/stauffApi';

export const useStauff = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [availability, setAvailability] = useState(null);

  const searchParts = useCallback(async (partNumber) => {
    if (!partNumber?.trim()) {
      setError('Part number is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await stauffApi.searchParts(partNumber.trim());
      
      if (result.success) {
        setProducts(result.products || []);
        setAvailability(null); // Clear previous availability
      } else {
        setError(result.error || 'Search failed');
        setProducts([]);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      setError(errorMessage);
      setProducts([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const checkAvailability = useCallback(async (sku, quantity = 1) => {
    if (!sku?.trim()) {
      setError('SKU is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await stauffApi.checkAvailability(sku.trim(), quantity);
      
      if (result.success) {
        setAvailability(result.availability || null);
      } else {
        setError(result.error || 'Availability check failed');
        setAvailability(null);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      setError(errorMessage);
      setAvailability(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const searchWithAvailability = useCallback(async (partNumber, quantity = 1) => {
    if (!partNumber?.trim()) {
      setError('Part number is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await stauffApi.searchWithAvailability(partNumber.trim(), quantity);
      
      if (result.success) {
        setProducts(result.products || []);
        setAvailability(result.availability || null);
      } else {
        setError(result.error || 'Search failed');
        setProducts([]);
        setAvailability(null);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      setError(errorMessage);
      setProducts([]);
      setAvailability(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const checkCombinedStock = useCallback(async (partNumber, quantity = 1) => {
    if (!partNumber?.trim()) {
      setError('Part number is required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await stauffApi.checkCombinedStock(partNumber.trim(), quantity);
      
      if (result.success) {
        // Transform the combined result into the expected availability format
        const combinedAvailability = {
          // Keep existing Stauff data structure but add local stock info
          ...result.stauffStock,
          localStock: result.localStock,
          combinedAvailability: result.combinedAvailability,
          errors: result.errors
        };
        
        setAvailability(combinedAvailability);
      } else {
        setError(result.error || 'Combined stock check failed');
        setAvailability(null);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Network error occurred';
      setError(errorMessage);
      setAvailability(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setProducts([]);
    setAvailability(null);
    setError(null);
  }, []);

  return {
    loading,
    error,
    products,
    availability,
    searchParts,
    checkAvailability,
    searchWithAvailability,
    checkCombinedStock,
    clearResults
  };
};
